/**
 * Note number <-> letter, in ONE place (2026-08-10).
 *
 * Two surfaces now need this - the inspector's Send browser and the Matrix
 * picker - and they must agree, because between them they read a letter from
 * the rig map and write a number onto an item. Two copies of the convention
 * is how a note ends up one octave out on one screen and not the other.
 *
 * THE CONVENTION IS ABLETON'S: C3 = 60, the same one PianoItem uses
 * (`(rootOctave + 2) * 12`). It is a choice, not a law - the Matrix schema is
 * explicit that manufacturers offset by +/-12 and that the LETTER is the
 * identity while `noteMidi` is bookkeeping. So:
 *
 *   - matching AGAINST the rig map goes by letter, and tries BOTH spellings,
 *     because the map is hand-authored and the app is itself inconsistent
 *     (`NOTE_NAMES` in autotune/session.ts spells flats; the schema's own
 *     example is "F#4");
 *   - writing to an ITEM goes by number, derived here.
 */

/** Sharp spelling. Used for display, matching the schema's "F#4" example. */
const SHARP = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
/** Flat spelling. Only ever used for MATCHING a hand-authored label. */
const FLAT = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'];

function octaveOf(n: number): number { return Math.floor(n / 12) - 2; }

/** Display label for a MIDI note number. Sharp spelling. */
export function noteLabelFor(n: number): string {
  return `${SHARP[n % 12]}${octaveOf(n)}`;
}

/** Every spelling of a note number that a rig map might plausibly use. */
export function noteSpellings(n: number): string[] {
  const oct = octaveOf(n);
  const a = `${SHARP[n % 12]}${oct}`;
  const b = `${FLAT[n % 12]}${oct}`;
  return a === b ? [a] : [a, b];
}

/**
 * Parse a letter label back to a MIDI note number, or null when it is not a
 * note label at all. Accepts either spelling and a negative octave ("Bb-1").
 *
 * Returns null rather than a fallback number on purpose: a label the app
 * cannot parse must not silently become note 0. The caller decides what to do
 * with an unparseable row, and the Matrix deliberately allows labels this
 * function will reject (the schema notes TouchOSC and others sometimes use
 * unusual encodings, and the picker still has to SHOW those rows).
 */
export function noteNumberFor(label: string): number | null {
  const m = /^([A-G])([#b]?)(-?\d+)$/i.exec(label.trim());
  if (!m) return null;
  const letter = m[1]!.toUpperCase();
  const accidental = m[2] ?? '';
  const octave = Number.parseInt(m[3]!, 10);
  if (!Number.isFinite(octave)) return null;
  const spelling = accidental === 'b' || accidental === 'B'
    ? `${letter}b`
    : `${letter}${accidental}`;
  let pc = SHARP.indexOf(spelling);
  if (pc < 0) pc = FLAT.indexOf(spelling);
  if (pc < 0) return null;
  const n = (octave + 2) * 12 + pc;
  return n >= 0 && n <= 127 ? n : null;
}
